import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { MatButtonModule } from '@angular/material/button';
import { Subscription } from 'rxjs';
import { SessionService } from 'src/app/authentication/session.service';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import { CoreTargetService } from 'src/app/core/services/target.service';

type AuthView = 'login' | 'signup' | 'twoFactor' | 'resetPassword';

const TOO_MANY_ATTEMPTS =
  'Too many failed login attempts. Please try again later.';

// Login / signup page for users arriving from Agentic Monitoring ads.
// Reached via /agentic-monitoring-login, /agentic-monitoring-signup, or any
// URL carrying ?agentic-monitoring-login (see AppComponent + LoginComponent).
@Component({
  selector: 'app-agentic-monitoring-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatButtonModule],
  templateUrl: './login.component.html',
  styleUrls: [
    '../organic/signup/signup.component.css',
    '../login/login.component.css',
    '../organic/login/login.component.css',
    './login.component.css',
  ],
})
export class AgenticMonitoringLoginComponent implements OnInit, OnDestroy {
  constructor(
    private sessionService: SessionService,
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private titleService: Title,
    private localStorageService: LocalStorageService,
    private targetService: CoreTargetService,
  ) {}

  private subs = new Subscription();
  private googleAuthListener?: (event: MessageEvent) => void;

  view: AuthView = 'login';
  loading: boolean = false;
  moreInfo: string = '';
  showPassword: boolean = false;
  showSignupInputs: boolean = false;

  resetPasswordFinished: boolean = false;
  resetPasswordStatus: string =
    'Please type in your email address associated with your account.';

  loginForm = this.fb.group({
    email: ['', Validators.required],
    password: ['', Validators.required],
  });
  signupForm = this.fb.group({
    fullname: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    password: ['', Validators.required],
  });
  twoFactorForm = this.fb.group({
    twoFARequired: ['', [Validators.required, Validators.minLength(6)]],
  });
  resetPasswordForm = this.fb.group({
    email: ['', Validators.required],
  });

  ngOnInit(): void {
    document.body.classList.remove('dark-theme');
    // Leftovers from an earlier Organic visit must not send this user to the
    // Organic login, billing or onboarding.
    this.sessionService.clearOrganicEntryState();

    if (this.route.snapshot.data['mode'] === 'signup') {
      this.view = 'signup';
    }
    this.updateTitle();

    // Messages left by SessionService.navigate()/logout(), e.g. "no privileges".
    this.subs.add(
      this.sessionService.getLoginMoreInfo().subscribe((info) => {
        this.moreInfo = info;
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.stopGoogleAuth();
  }

  setView(view: AuthView): void {
    this.view = view;
    this.moreInfo = '';
    this.showSignupInputs = false;
    this.resetPasswordFinished = false;
    this.updateTitle();
  }

  login(): void {
    if (this.loginForm.invalid) {
      this.moreInfo = 'Enter your e-mail and password.';
      return;
    }
    this.startRequest();

    const email = this.loginForm.value.email?.toLowerCase() || '';
    const password = this.loginForm.value.password || '';

    this.subs.add(
      this.sessionService.authenticated(email, password).subscribe({
        next: (data: any) => {
          if (data?.extra_info) {
            this.fail(data.extra_info);
          } else if (data?.twoFARequired) {
            this.loading = false;
            this.view = 'twoFactor';
          } else {
            this.completeLogin(data, 'agentic-monitoring');
          }
        },
        error: (err) => this.fail(err?.error?.message),
      }),
    );
  }

  // Google sign-in in a popup (the same OAuth endpoint and "GoogleLogin"
  // message as the Organic pages). The form isn't locked while the popup is
  // open, so closing it leaves nothing stuck; clicking again starts over.
  loginWithGoogle(
    event: Event,
    redirectTo: string = 'agentic-monitoring',
  ): void {
    event.preventDefault();
    if (this.loading) return;
    this.stopGoogleAuth();
    this.moreInfo = '';

    const signupIntent = redirectTo.endsWith('/setup')
      ? '&signup_agentic_monitoring=true'
      : '';
    const popup = window.open(
      'https://api.dashboard.behamics.com/api/auth/google?origin=' +
        location.origin +
        signupIntent,
      'googleAuth',
      'popup=1,width=600,height=700',
    );

    if (!popup) {
      this.fail(
        "Couldn't open Google sign-in. Please allow pop-ups and try again.",
      );
      return;
    }

    const listener = (messageEvent: MessageEvent) => {
      // Only the popup's answer counts: other frames on the page (Intercom,
      // extensions) post messages here too.
      if (
        messageEvent.source !== popup ||
        messageEvent.data?.type !== 'GoogleLogin'
      ) {
        return;
      }

      this.stopGoogleAuth();
      popup.close();
      const { result } = messageEvent.data;
      if (result?.status === 3) {
        this.startRequest();
        this.completeLogin(result, redirectTo);
      } else {
        this.fail(result?.message);
      }
    };
    window.addEventListener('message', listener);
    this.googleAuthListener = listener;
  }

  private stopGoogleAuth(): void {
    if (!this.googleAuthListener) return;
    window.removeEventListener('message', this.googleAuthListener);
    this.googleAuthListener = undefined;
  }

  continueSignupWithEmail(event: Event): void {
    if (this.showSignupInputs) return;
    event.preventDefault();
    this.showSignupInputs = true;
  }

  submitTwoFactorCode(): void {
    if (this.twoFactorForm.invalid) return;
    this.startRequest();

    const email = this.loginForm.value.email?.toLowerCase() || '';
    const password = this.loginForm.value.password || '';
    const code = this.twoFactorForm.value.twoFARequired || '';

    this.subs.add(
      this.sessionService.authenticated(email, password, code).subscribe({
        next: (data: any) => {
          if (data?.status === 3) {
            this.completeLogin(data, 'agentic-monitoring');
          } else {
            this.fail('Invalid or expired 2FA code');
          }
        },
        error: (err) =>
          this.fail(
            err?.error?.message === TOO_MANY_ATTEMPTS
              ? TOO_MANY_ATTEMPTS
              : 'Invalid or expired 2FA code',
          ),
      }),
    );
  }

  signup(): void {
    if (this.signupForm.invalid) {
      this.moreInfo = 'Enter your full name, a valid e-mail and a password.';
      return;
    }
    this.startRequest();

    const { fullname, email, password } = this.signupForm.value;

    this.subs.add(
      this.sessionService
        // signup_agentic_monitoring: tells the backend which product the
        // account is for (backend support pending).
        .signup({ fullname, email, password, signup_agentic_monitoring: true })
        .subscribe({
          next: (res) => {
            if (res?.message?.toLowerCase() !== 'success') {
              this.fail(res?.message);
              return;
            }

            this.subs.add(
              this.sessionService
                .authenticated(email?.toLowerCase() || '', password || '')
                .subscribe({
                  next: (data: any) => {
                    if (data?.status === 3) {
                      this.completeLogin(data, 'agentic-monitoring/setup');
                    } else {
                      this.fail();
                    }
                  },
                  error: () => this.fail(),
                }),
            );
          },
          error: (err) => this.fail(err?.error?.message),
        }),
    );
  }

  resetPassword(): void {
    const email = this.resetPasswordForm.value.email || '';
    if (!email || this.resetPasswordFinished) return;
    this.startRequest();

    this.subs.add(
      this.sessionService.resetRequest(email.toLowerCase()).subscribe({
        next: (data: any) => {
          this.loading = false;
          if (data?.status == 200) {
            this.resetPasswordFinished = true;
            this.resetPasswordStatus =
              'The password reset verification has been sent, please check your e-mail address.';
          } else if (data?.status == 400) {
            this.resetPasswordStatus =
              'There is no user account with that e-mail address.';
          }
        },
        error: () => this.fail(),
      }),
    );
  }

  private completeLogin(data: any, redirectTo: string): void {
    // An account that also has an Organic workspace (Workspace_…) opens its
    // Agentic Monitoring one (am_…).
    this.sessionService.login(data, { preferTargetPrefix: 'am_' });
    // login() can set Organic flags again (phone verification, premium
    // signup); they don't apply to this product.
    this.sessionService.clearOrganicEntryState();
    // Keeps later /login visits (expired session, logout) on this page.
    this.localStorageService.set('agenticMonitoringLogin', true);
    this.loading = false;

    // Non-Behamics users with an Agentic Monitoring workspace go to its setup
    // page, never to an Organic page.
    const email = String(data?.user?.email || '').toLowerCase();
    const target = String(this.targetService.getSelectedTarget('target') || '');
    if (!email.endsWith('@behamics.com') && target.startsWith('am_')) {
      redirectTo = 'agentic-monitoring/setup';
    }
    this.router.navigate([redirectTo]);
  }

  private startRequest(): void {
    this.loading = true;
    this.moreInfo = '';
  }

  private fail(message?: string): void {
    this.loading = false;
    this.moreInfo = message || 'Something went wrong!';
  }

  private updateTitle(): void {
    this.titleService.setTitle(
      (this.view === 'signup' ? 'Signup' : 'Login') + ' - Agentic Monitoring',
    );
  }
}
